import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { CatalogSong } from '../../../../redux/api/fymAPI.types';
import { catalogSongMessages } from './catalogSongMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the song files; each part uses its own slice of it */
import styles from './SongFiles.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SongMadeListProps {
  /** The song title. The kit row opens "<title>" (the song page). */
  title: string;
  made: CatalogSong['made'];
  onOpenSong: () => void;
  onOpenReels: () => void;
  onOpenCalendar: () => void;
}

/**
 * "Made from this song" (design f2): the kit assets (they open the song page in Releases), the AI
 * reels (they open the same song page at the AI reel card) and the posts of this row. Clip Studio is gone
 * (D24), so the clip count is not shown.
 */
const SongMadeListComponent: React.FC<SongMadeListProps> = ({
  title,
  made,
  onOpenSong,
  onOpenReels,
  onOpenCalendar,
}) => {
  const intl = useIntl();

  const rows = useMemo(
    () =>
      [
        {
          key: 'kitAssets',
          count: made.kitAssets,
          title: intl.formatMessage(catalogSongMessages.madeKitAssets, { count: made.kitAssets }),
          source: intl.formatMessage(catalogSongMessages.madeKitAssetsSource),
          openLabel: intl.formatMessage(catalogSongMessages.openSong, { title }),
          onOpen: onOpenSong,
        },
        {
          key: 'reels',
          count: made.reels,
          title: intl.formatMessage(catalogSongMessages.madeReels, { count: made.reels }),
          // The reels row opens the song page in Releases too, so it names Releases.
          source: intl.formatMessage(catalogSongMessages.madeKitAssetsSource),
          openLabel: intl.formatMessage(catalogSongMessages.open),
          onOpen: onOpenReels,
        },
        {
          key: 'posts',
          count: made.posts,
          title: intl.formatMessage(catalogSongMessages.madePosts, { count: made.posts }),
          source: intl.formatMessage(catalogSongMessages.madePostsSource),
          openLabel: intl.formatMessage(catalogSongMessages.open),
          onOpen: onOpenCalendar,
        },
      ].filter((row) => row.count > 0),
    [intl, made.kitAssets, made.posts, made.reels, onOpenCalendar, onOpenReels, onOpenSong, title]
  );

  return (
    <div className={styles.field}>
      <span className={styles.label}>{intl.formatMessage(catalogSongMessages.made)}</span>
      {rows.length === 0 ? (
        <span className={styles.small}>{intl.formatMessage(catalogSongMessages.madeNothing)}</span>
      ) : (
        <div className={styles.made}>
          {rows.map((row) => (
            <div key={row.key} className={styles.madeRow}>
              <div className={styles.grow}>
                <span className={styles.strong}>{row.title}</span>
                <span className={styles.mono}>{row.source}</span>
              </div>
              <button type="button" className={styles.linkButton} onClick={row.onOpen}>
                {row.openLabel}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const SongMadeList = TypedMemo(SongMadeListComponent);
